import './styles.css';

import * as React from 'react';
import { Prompt, RouteComponentProps } from 'react-router';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import Grid from '@material-ui/core/Grid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { Header } from '../Header/Header';
import { Loader } from '../Loader/Loader';
import tracker from '../Loader/loader-tracker';
import { SalesStore } from '../../stores/sales-store';
import { UiStore } from '../../stores/ui-store';
import { OrderUiState } from '../../stores/ui-state/order-ui-state';
import { Order } from '../../models/order';
import { Address } from '../../models/address';
import CreditRequestProductLines from './CreditRequestProductLines';
import CurrencyField, { ICurrencyChangedEvent } from '../Currency/CurrencyField';
import TextField from '@material-ui/core/TextField';
import OrderSummary from '../Order/OrderSummary';
import { round } from '../../math';
import {
    CreditRequestFreightChargeMode,
    CreditRequestModel,
    CreditRequestProductLineModel,
    creditRequestStatusLabel,
    ICreditRequestAddress,
    ICreditRequestDetailResponse,
    toAddressLocationType,
    toCreditRequestLocationType
} from '../../models/credit-request';
import { CreditRequestApi } from '../../services/credit-request-api';
import { CreditRequestActions } from './CreditRequestActions';
import { CashCustomerBankDetails } from './CashCustomerBankDetails';
import CreditRequestAuditInfo from './CreditRequestAuditInfo';
import { CreditRequestReturnAddresses } from './CreditRequestReturnAddresses';
import {
    calculateCustomerCredit,
    calculateCustomerCreditIncludingGst,
    calculateProductRefundAmount,
    calculateProductRestockingFee,
    calculateTotalRestockingFee,
    customerCreditExceedsPaid
} from '../../models/credit-request-calculator';

export interface ICreditRequestProps {
    salesStore?: SalesStore;
    uiStore?: UiStore;
    creditRequestApi?: CreditRequestApi;
}

interface MatchParams {
    orderNumber?: string;
    requestId?: string;
}

interface IProductRestockingFee {
    applied: boolean;
    percentage: number;
}

type RequiredYesNo = boolean | null;
type FreightChargeMode = '' | 'deduct' | 'separate';

type Props = ICreditRequestProps & RouteComponentProps<MatchParams>;

@inject('salesStore', 'uiStore')
@observer
class CreditRequest extends React.Component<Props> {
    private salesStore: SalesStore;
    private uiStore: UiStore;
    private orderUiState: OrderUiState;
    private creditRequestApi: CreditRequestApi;
    @observable private creditRequest: CreditRequestModel | null = null;

    constructor(props: Props) {
        super(props);
        this.salesStore = props.salesStore!;
        this.uiStore = props.uiStore!;
        this.orderUiState = props.uiStore!.orderUiState;
        this.creditRequestApi = props.creditRequestApi || new CreditRequestApi();
    }

    public async componentDidMount() {
        window.onbeforeunload = () => this.creditRequest && this.creditRequest.isDirty
            ? true
            : void (0);
        this.orderUiState.setFormReadOnly(true);
        const { orderNumber, requestId } = this.props.match.params;
        let response: ICreditRequestDetailResponse | null = null;
        if (requestId) response = await tracker.track(this.creditRequestApi.get(Number(requestId)));
        const order = await tracker.track(this.salesStore.loadOrder(response ? response.orderNumber : orderNumber!));
        this.orderUiState.initViewOrder(order);
        this.startCreditRequest(order, response);
    }

    @action
    private startCreditRequest(order: Order, response: ICreditRequestDetailResponse | null) {
        this.creditRequest = response
            ? CreditRequestModel.fromResponse(response)
            : CreditRequestModel.forOrder(order);
        this.orderUiState.setFormReadOnly(!this.creditRequest.canEdit);
    }

    public componentWillUnmount() {
        window.onbeforeunload = null;
        this.orderUiState.setFormReadOnly(true);
    }

    private get request(): CreditRequestModel {
        return this.creditRequest!;
    }

    private get reason() { return this.request.reason; }
    private get customerCreditRequired() { return this.request.customerCreditRequired; }
    private get returnRequired() { return this.request.returnRequired; }
    private get freightRequired() { return this.request.freightRequired; }
    private get freightChargeMode(): FreightChargeMode {
        if (this.request.freightChargeMode === CreditRequestFreightChargeMode.Deduct) return 'deduct';
        if (this.request.freightChargeMode === CreditRequestFreightChargeMode.Separate) return 'separate';
        return '';
    }
    private get freightChargeTo(): string {
        if (this.freightChargeMode === 'deduct') return 'Take from refund';
        if (this.freightChargeMode === 'separate') return 'Charged on separate docket';
        return '';
    }
    private get freightAmount() { return this.request.freightAmount; }
    private get freightMojoeNumber() { return this.request.freightMojoeNumber; }
    private get freightNoReason() { return this.request.noFreightReason; }
    private get newPurchaseOrderRequired() { return this.request.newPurchaseOrderRequired; }
    private get newPurchaseOrderNoCharge() { return this.request.newPurchaseOrderNoCharge; }
    private get supplierContacted() { return this.request.supplierContacted; }
    private get supplierName() { return this.request.supplierName; }
    private get supplierContactName() { return this.request.supplierContactName; }
    private get supplierCreditRequired() { return this.request.supplierCreditRequired; }
    private get supplierCreditAmount() { return this.request.supplierCreditAmount; }
    private get supplierRestockingFeeRequired() { return this.request.supplierRestockingFeeRequired; }
    private get supplierRestockingFeeAmount() { return this.request.supplierRestockingFeeAmount; }
    private get fixedAmountCreditExGst() { return this.request.fixedAmountCreditExGst; }
    private get generalComment() { return this.request.generalComment; }
    private setReason(reason: string) {
        this.request.update('reason', reason);
    }

    private setFreightChargeMode(mode: FreightChargeMode) {
        const value = mode === 'deduct'
            ? CreditRequestFreightChargeMode.Deduct
            : CreditRequestFreightChargeMode.Separate;
        this.request.update('freightChargeMode', value);
        if (mode === 'separate') this.request.update('freightAmount', 0);
    }

    private setFreightAmount(amount: number) {
        this.request.update('freightAmount', amount);
    }

    private setFreightMojoeNumber(mojoeNumber: string) {
        this.request.update('freightMojoeNumber', mojoeNumber);
    }

    private setFreightNoReason(reason: string) {
        this.request.update('noFreightReason', reason);
    }

    private setSupplierCreditRequired(checked: boolean) {
        this.request.update('supplierCreditRequired', checked);
        if (!checked) this.request.update('supplierCreditAmount', 0);
    }

    private setSupplierCreditAmount(amount: number) {
        this.request.update('supplierCreditAmount', amount);
    }

    private setSupplierRestockingFeeRequired(checked: boolean) {
        this.request.update('supplierRestockingFeeRequired', checked);
        if (!checked) this.request.update('supplierRestockingFeeAmount', 0);
    }

    private setSupplierRestockingFeeAmount(amount: number) {
        this.request.update('supplierRestockingFeeAmount', amount);
    }

    private setFixedAmountCreditExGst(e: ICurrencyChangedEvent) {
        this.request.update('fixedAmountCreditExGst', round(e.parsedValue));
    }

    private setGeneralComment(comment: string) {
        this.request.update('generalComment', comment);
    }

    private setNewPurchaseOrderRequired(checked: boolean) {
        this.request.update('newPurchaseOrderRequired', checked);
        if (!checked) this.request.update('newPurchaseOrderNoCharge', null);
    }

    private setNewPurchaseOrderNoCharge(checked: boolean) {
        this.request.update('newPurchaseOrderNoCharge', checked);
    }

    private setSupplierContacted(checked: boolean) {
        this.request.update('supplierContacted', checked);
        if (!checked) {
            this.request.update('supplierName', '');
            this.request.update('supplierContactName', '');
        }
    }

    private setSupplierName(name: string) {
        this.request.update('supplierName', name);
    }

    private setSupplierContactName(name: string) {
        this.request.update('supplierContactName', name);
    }

    private setFreightRequired(checked: boolean) {
        this.request.update('freightRequired', checked);
        if (!checked) {
            this.request.update('freightChargeMode', null);
            this.request.update('freightAmount', 0);
            this.request.update('freightMojoeNumber', '');
        } else {
            this.request.update('noFreightReason', '');
        }
    }

    private setCustomerCreditRequired(checked: boolean) {
        this.request.update('customerCreditRequired', checked);
    }

    @action
    private setReturnRequired(checked: boolean) {
        const { order } = this.salesStore;
        this.request.update('returnRequired', checked);
        if (checked && !this.request.pickupAddress) {
            const address = this.copyAddress(order.delivery.address);
            this.request.update('pickupAddress', this.addressToRequest(address));
        }
    }

    private setRestockingFeeApplied(productLine: CreditRequestProductLineModel, checked: boolean) {
        this.request.updateProductLine(
            productLine.sourceOrderProductItemId,
            'restockingFeeApplied',
            checked
        );
    }

    private setRestockingFeePercentage(productLine: CreditRequestProductLineModel, percentage: number) {
        this.request.updateProductLine(
            productLine.sourceOrderProductItemId,
            'restockingFeePercentage',
            Math.min(100, Math.max(0, percentage))
        );
    }

    private setReturnQuantity(productLine: CreditRequestProductLineModel, quantity: number) {
        this.request.updateProductLine(productLine.sourceOrderProductItemId, 'returnQuantity', quantity);
    }

    private getReturnQuantity(productLine: CreditRequestProductLineModel): number {
        return productLine.returnQuantity;
    }

    private getReturnQuantityError(productLine: CreditRequestProductLineModel): string | null {
        const quantity = this.getReturnQuantity(productLine);
        return quantity >= 0 && quantity <= productLine.availableQuantity
            ? null
            : `Must be less than ${productLine.availableQuantity}`;
    }

    private getProductRestockingFee(productLine: CreditRequestProductLineModel): IProductRestockingFee {
        return {
            applied: productLine.restockingFeeApplied,
            percentage: productLine.restockingFeePercentage
        };
    }

    private copyAddress(address: Address): Address {
        return new Address({
            formattedAddress: address.formattedAddress,
            name: address.name,
            street: address.street,
            suburb: address.suburb,
            state: address.state,
            postcode: address.postcode,
            latLng: { ...address.latLng },
            locationType: address.locationType,
            exWorksId: address.exWorksId
        });
    }

    private addressFromRequest(address: ICreditRequestAddress): Address {
        return new Address({
            formattedAddress: address.formattedAddress,
            name: address.name,
            street: address.street,
            suburb: address.suburb,
            state: address.state,
            postcode: address.postcode,
            latLng: { ...address.latLng },
            locationType: toAddressLocationType(address.locationType),
            exWorksId: address.exWorksId
        });
    }

    private addressToRequest(address: Address): ICreditRequestAddress {
        return {
            formattedAddress: address.formattedAddress,
            name: address.name,
            street: address.street,
            suburb: address.suburb,
            state: address.state,
            postcode: address.postcode,
            latLng: { ...address.latLng },
            locationType: toCreditRequestLocationType(address.locationType),
            exWorksId: address.exWorksId
        };
    }

    private updatePickupAddress(address: Address): void {
        this.request.update('pickupAddress', this.addressToRequest(address));
    }

    private updateDestinationAddress(address: Address): void {
        this.request.update('destinationAddress', this.addressToRequest(address));
    }

    private get pickupAddress(): Address {
        return this.request.pickupAddress
            ? this.addressFromRequest(this.request.pickupAddress)
            : this.copyAddress(this.salesStore.order.delivery.address);
    }

    private get destinationAddress(): Address {
        return this.request.destinationAddress
            ? this.addressFromRequest(this.request.destinationAddress)
            : new Address({});
    }

    private async saveCreditRequest(): Promise<void> {
        try {
            const response = this.request.id === null
                ? await this.creditRequestApi.create({
                    orderId: this.request.orderId,
                    form: this.request.toSaveRequest()
                })
                : await this.creditRequestApi.save(this.request.id, this.request.toSaveRequest());
            this.request.markSaved(response);
            this.orderUiState.setFormReadOnly(!this.request.canEdit);
            if (!this.props.match.params.requestId) {
                this.props.history.replace(`/credit-request/${response.id}`);
            }
            this.uiStore.notificationUiState.showInfo('Credit request saved.');
        } catch {
            this.uiStore.notificationUiState.showError(
                'The credit request changed or could not be saved. Reload the page and try again.'
            );
        }
    }

    private formatCurrency(amount: number): string {
        const formattedAmount = Math.abs(amount).toFixed(2);
        return amount < 0 ? `-$${formattedAmount}` : `$${formattedAmount}`;
    }

    private yesNo(value: RequiredYesNo): string {
        if (value === null) return 'Not answered';
        return value ? 'Yes' : 'No';
    }

    private getRelatedPurchaseOrderNumbers(productLines: CreditRequestProductLineModel[]): string[] {
        return productLines
            .map(productLine => productLine.purchaseOrderNumber)
            .filter((purchaseOrderNumber, index, allPurchaseOrderNumbers) =>
                !!purchaseOrderNumber && allPurchaseOrderNumbers.indexOf(purchaseOrderNumber) === index
            );
    }

    private getDeductedFreightAmount(): number {
        return this.freightRequired && this.freightChargeMode === 'deduct'
            ? this.freightAmount
            : 0;
    }

    private getSupplierRestockingFeeAmount(): number {
        return this.supplierRestockingFeeRequired === true
            ? this.supplierRestockingFeeAmount
            : 0;
    }

    private hasFixedAmountCredit(): boolean {
        return this.fixedAmountCreditExGst !== 0;
    }

    private getCalculatedCreditAmountExGst(productLines: CreditRequestProductLineModel[]): number {
        const productReturnValue = this.getTotalProductReturnValue(productLines);
        const restockingFee = Math.max(0, this.getTotalRestockingFee(productLines) + this.getSupplierRestockingFeeAmount());
        const freightAmount = Math.max(0, this.getDeductedFreightAmount());
        return calculateCustomerCredit(productReturnValue, restockingFee, freightAmount);
    }

    private getTotalCreditAmountExGst(calculatedCreditExGst: number): number {
        return this.hasFixedAmountCredit()
            ? round(this.fixedAmountCreditExGst)
            : round(calculatedCreditExGst);
    }

    private getAmountPaid(): number {
        return Math.max(0, this.request.amountPaid || 0);
    }

    private returnQuantityIsValid(productLine: CreditRequestProductLineModel): boolean {
        const returnQuantity = this.getReturnQuantity(productLine);
        return !returnQuantity ||
            (returnQuantity > 0 && returnQuantity <= productLine.availableQuantity);
    }

    private hasInvalidReturnQuantity(): boolean {
        return this.request.productLines
            .some(productLine => !this.returnQuantityIsValid(productLine));
    }

    private getReturnedProductLines(): CreditRequestProductLineModel[] {
        return this.request.productLines
            .filter(productLine =>
                this.getReturnQuantity(productLine) > 0 && this.returnQuantityIsValid(productLine)
            );
    }

    private getProductTotalCredited(productLine: CreditRequestProductLineModel): number {
        return this.returnQuantityIsValid(productLine)
            ? productLine.unitPrice * this.getReturnQuantity(productLine)
            : 0;
    }

    private getTotalProductReturnValue(productLines: CreditRequestProductLineModel[]): number {
        return round(productLines
            .map(productLine => this.getProductTotalCredited(productLine))
            .reduce((total, productTotalCredited) => total + productTotalCredited, 0));
    }

    private renderRequiredYesNo(
        label: string,
        value: RequiredYesNo,
        onChange: (checked: boolean) => void,
        error?: string
    ) {
        return (
            <div className="required-yes-no">
                <p className="required-yes-no-label">{label}</p>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === true}
                            disabled={!this.request.canEdit}
                            onChange={() => onChange(true)}
                        />
                    }
                    label="Yes"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value === false}
                            disabled={!this.request.canEdit}
                            onChange={() => onChange(false)}
                        />
                    }
                    label="No"
                />
                {error && <p className="required-answer">{error}</p>}
            </div>
        );
    }

    private getRestockingFeeAmount(productLine: CreditRequestProductLineModel): number {
        const restockingFee = this.getProductRestockingFee(productLine);
        return calculateProductRestockingFee(
            this.getProductTotalCredited(productLine),
            restockingFee.applied,
            restockingFee.percentage
        );
    }

    private getTotalRestockingFee(productLines: CreditRequestProductLineModel[]): number {
        const restockingFees = productLines
            .map(productLine => this.getRestockingFeeAmount(productLine));
        return calculateTotalRestockingFee(restockingFees);
    }

    private getProductRefundAmount(productLine: CreditRequestProductLineModel): number {
        return calculateProductRefundAmount(
            this.getProductTotalCredited(productLine),
            this.getRestockingFeeAmount(productLine)
        );
    }

    private summaryRow(label: string, value: React.ReactNode, multiline: boolean = false) {
        return (
            <React.Fragment>
                <Grid item xs={9}><p><strong>{label}</strong></p></Grid>
                <Grid
                    item
                    xs={3}
                    className={`number-col${multiline ? ' multiline-summary-value' : ''}`}
                >
                    <p>{value}</p>
                </Grid>
            </React.Fragment>
        );
    }

    private renderSummary() {
        const returnedLines = this.getReturnedProductLines();
        const savedCalculation = this.request.id !== null && !this.request.isDirty
            ? this.request.calculation
            : null;
        const canCalculateReturnAmounts = savedCalculation
            ? !savedCalculation.hasInvalidReturnQuantity
            : !this.hasInvalidReturnQuantity();
        const productReturnValue = savedCalculation
            ? savedCalculation.productReturnValue
            : this.getTotalProductReturnValue(returnedLines);
        const restockingFee = savedCalculation
            ? savedCalculation.customerRestockingFee
            : this.getTotalRestockingFee(returnedLines);
        const supplierRestockingFee = savedCalculation
            ? savedCalculation.supplierRestockingFee
            : this.getSupplierRestockingFeeAmount();
        const deductedFreightAmount = savedCalculation
            ? savedCalculation.deductedFreight
            : this.getDeductedFreightAmount();
        const fixedAmountCreditSet = this.hasFixedAmountCredit();
        const calculatedCreditExGst = savedCalculation
            ? savedCalculation.calculatedCreditExGst
            : canCalculateReturnAmounts
                ? this.getCalculatedCreditAmountExGst(returnedLines)
                : 0;
        const totalCreditExGst = savedCalculation
            ? savedCalculation.totalCreditExGst
            : fixedAmountCreditSet
                ? this.getTotalCreditAmountExGst(calculatedCreditExGst)
                : calculatedCreditExGst;
        const canShowFinalCreditAmount = fixedAmountCreditSet || canCalculateReturnAmounts;
        const totalCreditInclGst = savedCalculation
            ? savedCalculation.totalCreditIncludingGst
            : canShowFinalCreditAmount
                ? calculateCustomerCreditIncludingGst(totalCreditExGst)
                : 0;
        const amountPaid = this.getAmountPaid();
        const exceedsPaidAmount = savedCalculation
            ? savedCalculation.hasCreditExceedingPaidWarning
            : canShowFinalCreditAmount && customerCreditExceedsPaid(totalCreditExGst, amountPaid);
        const negativeCashCredit = savedCalculation
            ? savedCalculation.hasNegativeCashCreditWarning
            : this.request.isCashCustomer && totalCreditExGst < 0;
        const negativeCashCreditClass = negativeCashCredit
            ? 'negative-customer-credit'
            : '';
        const creditAmountClass = negativeCashCreditClass || (exceedsPaidAmount ? 'negative-customer-credit' : '');
        const calculatedCurrency = (amount: number) =>
            canCalculateReturnAmounts ? this.formatCurrency(amount) : '—';
        const finalCurrency = (amount: number) =>
            canShowFinalCreditAmount ? this.formatCurrency(amount) : '—';

        return (
            <Grid container className="section">
                <Grid item xs={1} />
                <Grid item xs={10}>
                    <h2>Summary</h2>
                    <Grid container>
                        {this.summaryRow('Reason', this.reason || '—', true)}

                        {this.summaryRow('Customer credit required', this.yesNo(this.customerCreditRequired))}
                        {this.summaryRow('Product return required', this.yesNo(this.returnRequired))}
                        {this.returnRequired && this.summaryRow('Pickup address', this.pickupAddress.formattedAddress || '—')}
                        {this.returnRequired && this.summaryRow('Return address', this.destinationAddress.formattedAddress || '—')}
                        {this.returnRequired && this.summaryRow(
                            'Products to return',
                            returnedLines.length > 0
                                ? returnedLines.map(l => `${l.code} × ${this.getReturnQuantity(l)}`).join(', ')
                                : '—'
                        )}
                        {this.summaryRow('Freight required', this.yesNo(this.freightRequired))}
                        {this.freightRequired && this.summaryRow('Freight charge to', this.freightChargeTo || '—')}
                        {this.freightRequired && this.summaryRow('Freight Mojoe #', this.freightMojoeNumber || '—')}
                        {this.freightRequired === false && this.summaryRow(
                            'No freight reason',
                            this.freightNoReason || '—',
                            true
                        )}

                        {this.summaryRow('Supplier credit required', this.yesNo(this.supplierCreditRequired))}
                        {this.summaryRow('Supplier restocking fee', this.yesNo(this.supplierRestockingFeeRequired))}
                        {this.summaryRow('Purchase order required', this.yesNo(this.newPurchaseOrderRequired))}
                        {this.newPurchaseOrderRequired === true && this.summaryRow(
                            'Purchase order no charge',
                            this.yesNo(this.newPurchaseOrderNoCharge)
                        )}
                        {this.summaryRow('Supplier contacted', this.yesNo(this.supplierContacted))}
                        {this.supplierContacted === true && this.summaryRow('Supplier', this.supplierName || '—')}
                        {this.supplierContacted === true && this.summaryRow(
                            'Supplier contact spoken to',
                            this.supplierContactName || '—'
                        )}
                        {this.summaryRow('General Comment', this.generalComment || '—', true)}

                        {this.summaryRow('Amount paid', this.formatCurrency(amountPaid))}
                        {this.returnRequired && this.summaryRow(
                            'Product return value (ex GST)',
                            calculatedCurrency(productReturnValue)
                        )}
                        {this.returnRequired && this.summaryRow(
                            'Customer restocking fee (ex GST)',
                            calculatedCurrency(restockingFee)
                        )}
                        {this.supplierCreditRequired === true && this.summaryRow(
                            'Supplier credit amount (ex GST)',
                            this.formatCurrency(this.supplierCreditAmount)
                        )}
                        {this.supplierRestockingFeeRequired === true && this.summaryRow(
                            'Supplier restocking fee (ex GST)',
                            this.formatCurrency(supplierRestockingFee)
                        )}
                        {this.freightRequired && this.freightChargeMode === 'deduct' && this.summaryRow(
                            'Freight deducted from credit (ex GST)',
                            this.formatCurrency(deductedFreightAmount)
                        )}
                        {fixedAmountCreditSet && this.summaryRow(
                            'Calculated credit amount (ex GST)',
                            calculatedCurrency(calculatedCreditExGst)
                        )}
                        {fixedAmountCreditSet && this.summaryRow(
                            'Fixed amount credit (ex GST)',
                            this.formatCurrency(this.fixedAmountCreditExGst || 0)
                        )}
                        {this.summaryRow('Total credit amount (ex GST)', finalCurrency(totalCreditExGst))}
                        {this.summaryRow(
                            'Total credit amount (incl GST)',
                            <span className={creditAmountClass}>
                                {finalCurrency(totalCreditInclGst)}
                                {negativeCashCreditClass && (
                                    <span className="negative-credit-note">Amount to be charged to Customer</span>
                                )}
                                {exceedsPaidAmount && (
                                    <span className="negative-credit-note">
                                        Cannot credit more than the amount paid
                                    </span>
                                )}
                            </span>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    public render() {
        const { order } = this.salesStore;
        const orderNumber = order.orderNumber || this.props.match.params.orderNumber || '';
        if (!this.creditRequest) {
            return (
                <div className="content credit-request-page">
                    <Header title={`Credit Request - Order Number ${orderNumber}`} />
                    <Loader area="credit-request" />
                </div>
            );
        }

        return (
            <React.Fragment>
                <Prompt
                    when={this.request.isDirty}
                    message="You have unsaved changes. Are you sure you want to leave?"
                />
                <div className="content credit-request-page">
                    <Header title={`Credit Request - Order Number ${orderNumber}`} />
                    <Loader area="credit-request" />

                    <CreditRequestActions
                        api={this.creditRequestApi}
                        model={this.request}
                        notificationUiState={this.uiStore.notificationUiState}
                        onSave={() => this.saveCreditRequest()}
                    />

                    <fieldset
                        disabled={!this.request.canEdit}
                        style={{ border: 0, margin: 0, minWidth: 0, padding: 0 }}
                    >
                    <Grid container className="section">
                        <Grid item xs={1} />
                        <Grid item xs={10}>
                            <TextField
                                id="credit-request-status"
                                label="Credit Request Status"
                                value={creditRequestStatusLabel(this.request.status)}
                                disabled
                                fullWidth
                            />
                            <h2>Order Details</h2>
                            <Grid container>
                                <Grid item xs={9}><p><strong>Order Number</strong></p></Grid>
                                <Grid item xs={3} className="number-col"><p>{order.orderNumber}</p></Grid>
                                <Grid item xs={9}><p><strong>Customer</strong></p></Grid>
                                <Grid item xs={3} className="number-col"><p>{this.request.customerName}</p></Grid>
                                <Grid item xs={9}><p><strong>Delivered Address</strong></p></Grid>
                                <Grid item xs={3} className="number-col"><p>{this.request.deliveredAddress || '—'}</p></Grid>
                                <Grid item xs={9}><p><strong>Delivered Date</strong></p></Grid>
                                <Grid item xs={3} className="number-col">
                                    <p>
                                        {this.request.deliveredDate
                                            ? new Date(this.request.deliveredDate).toLocaleDateString('en-AU')
                                            : 'TBA'}
                                    </p>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {this.request.id === null && <OrderSummary />}

                    <React.Fragment>
                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Customer Credit</h2>
                                    {this.renderRequiredYesNo(
                                        'Is a credit required to the Customer?',
                                        this.customerCreditRequired,
                                        (checked: boolean) => this.setCustomerCreditRequired(checked),
                                        this.request.errors.customerCreditRequired
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Return Details</h2>
                                    {this.renderRequiredYesNo(
                                        'Is a return of product required?',
                                        this.returnRequired,
                                        (checked: boolean) => this.setReturnRequired(checked)
                                    )}

                                    {this.returnRequired && (
                                        <CreditRequestReturnAddresses
                                            pickupAddress={this.pickupAddress}
                                            destinationAddress={this.destinationAddress}
                                            pickupError={this.request.errors.pickupAddress}
                                            destinationError={this.request.errors.destinationAddress}
                                            disabled={!this.request.canEdit}
                                            onPickupAddressChange={(address: Address) =>
                                                this.updatePickupAddress(address)
                                            }
                                            onDestinationAddressChange={(address: Address) =>
                                                this.updateDestinationAddress(address)
                                            }
                                            setMapCoordinates={(fieldName: string, lat: number, lng: number) =>
                                                this.orderUiState.setLatLng(fieldName, { lat, lng })
                                            }
                                        />
                                    )}
                                </Grid>
                            </Grid>

                            {this.returnRequired && (
                                <CreditRequestProductLines
                                    productLines={this.request.productLines}
                                    disabled={!this.request.canEdit}
                                    getReturnQuantity={(productLine: CreditRequestProductLineModel) =>
                                        this.getReturnQuantity(productLine)
                                    }
                                    getReturnQuantityError={(productLine: CreditRequestProductLineModel) =>
                                        productLine.errors.returnQuantity ||
                                        this.getReturnQuantityError(productLine)
                                    }
                                    getRestockingFeeApplied={(productLine: CreditRequestProductLineModel) =>
                                        this.getProductRestockingFee(productLine).applied
                                    }
                                    getRestockingFeePercentage={(productLine: CreditRequestProductLineModel) =>
                                        this.getProductRestockingFee(productLine).percentage
                                    }
                                    getRestockingFeeAmount={(productLine: CreditRequestProductLineModel) =>
                                        this.getRestockingFeeAmount(productLine)
                                    }
                                    getReturnValue={(productLine: CreditRequestProductLineModel) =>
                                        this.getProductTotalCredited(productLine)
                                    }
                                    getRefundAmount={(productLine: CreditRequestProductLineModel) =>
                                        this.getProductRefundAmount(productLine)
                                    }
                                    onRestockingFeeAppliedChange={(
                                        productLine: CreditRequestProductLineModel,
                                        checked: boolean
                                    ) =>
                                        this.setRestockingFeeApplied(productLine, checked)
                                    }
                                    onRestockingFeePercentageChange={(
                                        productLine: CreditRequestProductLineModel,
                                        percentage: number
                                    ) =>
                                        this.setRestockingFeePercentage(productLine, percentage)
                                    }
                                    onReturnQuantityChange={(
                                        productLine: CreditRequestProductLineModel,
                                        quantity: number
                                    ) =>
                                        this.setReturnQuantity(productLine, quantity)
                                    }
                                />
                            )}

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Fixed Credit Amount</h2>
                                    <div className="fixed-credit-amount-field">
                                        <CurrencyField
                                            name="fixedAmountCreditExGst"
                                            label="Fixed amount credit (ex GST)"
                                            value={this.fixedAmountCreditExGst}
                                            disabled={!this.request.canEdit}
                                            onChange={(e: ICurrencyChangedEvent) => {
                                                this.setFixedAmountCreditExGst(e);
                                            }}
                                        />
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Reason</h2>
                                    <TextField
                                        label="What is the reason for credit/return?"
                                        value={this.reason}
                                        disabled={!this.request.canEdit}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            this.setReason(e.currentTarget.value);
                                        }}
                                        multiline
                                        rows={6}
                                        fullWidth
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>

                            {this.request.isCashCustomer && (
                                <CashCustomerBankDetails
                                    model={this.request}
                                    disabled={!this.request.canEdit}
                                />
                            )}

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Freight</h2>
                                    {this.renderRequiredYesNo(
                                        'Do we need to charge freight?',
                                        this.freightRequired,
                                        (checked: boolean) => this.setFreightRequired(checked),
                                        this.request.errors.freightRequired
                                    )}
                                    {this.freightRequired && (
                                        <React.Fragment>
                                            <div className="freight-charge-options">
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.freightChargeMode === 'deduct'}
                                                            disabled={!this.request.canEdit}
                                                            onChange={() => this.setFreightChargeMode('deduct')}
                                                        />
                                                    }
                                                    label="Take from refund (Cash Customer)"
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={this.freightChargeMode === 'separate'}
                                                            disabled={!this.request.canEdit}
                                                            onChange={() => this.setFreightChargeMode('separate')}
                                                        />
                                                    }
                                                    label="Charged on separate docket (Account Customer)"
                                                />
                                            </div>
                                            {this.request.errors.freightChargeMode && (
                                                <p className="required-answer">
                                                    {this.request.errors.freightChargeMode}
                                                </p>
                                            )}
                                            {this.freightChargeMode === 'deduct' && (
                                                <div className="freight-amount-field">
                                                    <CurrencyField
                                                        name="freightAmount"
                                                        label="Amount"
                                                        value={this.freightAmount}
                                                        disabled={!this.request.canEdit}
                                                        onChange={(e: ICurrencyChangedEvent) => {
                                                            this.setFreightAmount(e.parsedValue);
                                                        }}
                                                        error={this.request.errors.freightAmount}
                                                    />
                                                </div>
                                            )}
                                            {!!this.freightChargeMode && (
                                                <TextField
                                                    label="Mojoe #"
                                                    value={this.freightMojoeNumber}
                                                    disabled={!this.request.canEdit}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        this.setFreightMojoeNumber(e.currentTarget.value);
                                                    }}
                                                    error={!!this.request.errors.freightMojoeNumber}
                                                    helperText={this.request.errors.freightMojoeNumber || null}
                                                    fullWidth
                                                />
                                            )}
                                        </React.Fragment>
                                    )}
                                    {this.freightRequired === false && (
                                        <TextField
                                            label="Reason"
                                            value={this.freightNoReason}
                                            disabled={!this.request.canEdit}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                this.setFreightNoReason(e.currentTarget.value);
                                            }}
                                            error={!!this.request.errors.noFreightReason}
                                            helperText={this.request.errors.noFreightReason || null}
                                            multiline
                                            rows={4}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Supplier Credit</h2>
                                    {this.getRelatedPurchaseOrderNumbers(this.request.productLines).length > 0 && (
                                        <div className="related-purchase-orders">
                                            <p><strong>Related PO number(s)</strong></p>
                                            <p>{this.getRelatedPurchaseOrderNumbers(this.request.productLines).join(', ')}</p>
                                        </div>
                                    )}
                                    {this.renderRequiredYesNo(
                                        'Is a credit required from Supplier?',
                                        this.supplierCreditRequired,
                                        (checked: boolean) => this.setSupplierCreditRequired(checked),
                                        this.request.errors.supplierCreditRequired
                                    )}
                                    {this.supplierCreditRequired === true && (
                                        <div className="supplier-credit-amount-field">
                                            <CurrencyField
                                                name="supplierCreditAmount"
                                                label="Credit Amount (ex GST)"
                                                value={this.supplierCreditAmount}
                                                disabled={!this.request.canEdit}
                                                onChange={(e: ICurrencyChangedEvent) => {
                                                    this.setSupplierCreditAmount(e.parsedValue);
                                                }}
                                                error={this.request.errors.supplierCreditAmount}
                                            />
                                        </div>
                                    )}
                                    <div className="supplier-restocking-fee">
                                        {this.renderRequiredYesNo(
                                            'Is there a Supplier Restocking Fee?',
                                            this.supplierRestockingFeeRequired,
                                            (checked: boolean) => this.setSupplierRestockingFeeRequired(checked),
                                            this.request.errors.supplierRestockingFeeRequired
                                        )}
                                        {this.supplierRestockingFeeRequired === true && (
                                            <div className="supplier-restocking-fee-amount-field">
                                                <CurrencyField
                                                    name="supplierRestockingFeeAmount"
                                                    label="Supplier Restocking Fee (ex GST)"
                                                    value={this.supplierRestockingFeeAmount}
                                                    disabled={!this.request.canEdit}
                                                    onChange={(e: ICurrencyChangedEvent) => {
                                                        this.setSupplierRestockingFeeAmount(e.parsedValue);
                                                    }}
                                                    error={this.request.errors.supplierRestockingFeeAmount}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Supplier Contact</h2>
                                    {this.renderRequiredYesNo(
                                        'Has the supplier been contacted?',
                                        this.supplierContacted,
                                        (checked: boolean) => this.setSupplierContacted(checked),
                                        this.request.errors.supplierContacted
                                    )}
                                    {this.supplierContacted === true && (
                                        <React.Fragment>
                                            <TextField
                                                label="Supplier"
                                                value={this.supplierName}
                                                disabled={!this.request.canEdit}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    this.setSupplierName(e.currentTarget.value);
                                                }}
                                                error={!!this.request.errors.supplierName}
                                                helperText={this.request.errors.supplierName || null}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Supplier Contact Name"
                                                value={this.supplierContactName}
                                                disabled={!this.request.canEdit}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    this.setSupplierContactName(e.currentTarget.value);
                                                }}
                                                error={!!this.request.errors.supplierContactName}
                                                helperText={this.request.errors.supplierContactName || null}
                                                fullWidth
                                            />
                                        </React.Fragment>
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>Purchase Order</h2>
                                    {this.renderRequiredYesNo(
                                        'Do we need to raise a new purchase order?',
                                        this.newPurchaseOrderRequired,
                                        (checked: boolean) => this.setNewPurchaseOrderRequired(checked),
                                        this.request.errors.newPurchaseOrderRequired
                                    )}
                                    {this.newPurchaseOrderRequired === true && (
                                        this.renderRequiredYesNo(
                                            'Is it at no charge?',
                                            this.newPurchaseOrderNoCharge,
                                            (checked: boolean) => this.setNewPurchaseOrderNoCharge(checked),
                                            this.request.errors.newPurchaseOrderNoCharge
                                        )
                                    )}
                                </Grid>
                            </Grid>

                            <Grid container className="section">
                                <Grid item xs={1} />
                                <Grid item xs={10}>
                                    <h2>General Comment</h2>
                                    <TextField
                                        id="credit-request-general-comment"
                                        label="General Comment"
                                        value={this.generalComment}
                                        disabled={!this.request.canEdit}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            this.setGeneralComment(e.currentTarget.value);
                                        }}
                                        multiline
                                        rows={6}
                                        fullWidth
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>

                            {this.renderSummary()}
                    </React.Fragment>
                    </fieldset>
                    <CreditRequestAuditInfo auditEntries={this.request.auditEntries} />
                </div>
            </React.Fragment>
        );
    }
}

export default CreditRequest;
